// src/features/tuition/services/subjectsService.js
import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    addDoc, 
    updateDoc, 
    deleteDoc,
    query, 
    where, 
    orderBy, 
    serverTimestamp 
  } from "firebase/firestore";
  import { 
    ref, 
    uploadBytes, 
    getDownloadURL,
    deleteObject 
  } from "firebase/storage";
  import { db, storage } from "../../../firebase/config";
  import { normalizeSubjectDocument } from "../../../utils/subjectDocument";
  
  // Fetch all subjects for a user
  export const fetchSubjects = async (userId) => {
    try {
      const subjectsRef = collection(db, "subjects");
      const q = query(
        subjectsRef,
        where("userId", "==", userId),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const subjects = [];
      
      querySnapshot.forEach((doc) => {
        subjects.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return subjects;
    } catch (error) {
      console.error("Error fetching subjects:", error);
      throw error;
    }
  };
  
  // Get a single subject by ID
  export const getSubject = async (subjectId) => {
    try {
      const subjectRef = doc(db, "subjects", subjectId);
      const subjectDoc = await getDoc(subjectRef);
      
      if (!subjectDoc.exists()) {
        throw new Error("Subject not found");
      }
      
      return {
        id: subjectDoc.id,
        ...subjectDoc.data()
      };
    } catch (error) {
      console.error("Error getting subject:", error);
      throw error;
    }
  };
  
  // Create a new subject
  export const createSubject = async (userId, subjectData) => {
    try {
      // Handle image upload if provided as a File
      let imageUrl = subjectData.image;
      
      if (subjectData.image instanceof File) {
        const storageRef = ref(storage, `subjects/${userId}/${subjectData.image.name}_${Date.now()}`);
        await uploadBytes(storageRef, subjectData.image);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      const normalizedSubject = normalizeSubjectDocument({
        ...subjectData,
        image: imageUrl,
        completed: 0,
        progress: 0
      }, { userId });

      const subjectsRef = collection(db, "subjects");
      const docRef = await addDoc(subjectsRef, {
        ...normalizedSubject,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return {
        id: docRef.id,
        ...normalizedSubject
      };
    } catch (error) {
      console.error("Error creating subject:", error);
      throw error;
    }
  };
  
  // Update an existing subject
  export const updateSubject = async (subjectId, subjectData) => {
    try {
      const subjectRef = doc(db, "subjects", subjectId);
      const currentSubject = await getDoc(subjectRef);

      if (!currentSubject.exists()) {
        throw new Error("Subject not found");
      }
      
      // Handle image upload if provided as a File
      let imageUrl = subjectData.image;
      
      if (subjectData.image instanceof File) {
        const userId = subjectData.userId;
        const storageRef = ref(storage, `subjects/${userId}/${subjectData.image.name}_${Date.now()}`);
        await uploadBytes(storageRef, subjectData.image);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      const updatedData = normalizeSubjectDocument({
        ...currentSubject.data(),
        ...subjectData,
        image: imageUrl,
      });
      
      await updateDoc(subjectRef, {
        ...updatedData,
        updatedAt: serverTimestamp()
      });
      
      return {
        id: subjectId,
        ...updatedData
      };
    } catch (error) {
      console.error("Error updating subject:", error);
      throw error;
    }
  };
  
  // Delete a subject
  export const deleteSubject = async (subjectId) => {
    try {
      // Get the subject to check if it has an image to delete
      const subjectRef = doc(db, "subjects", subjectId);
      const subjectDoc = await getDoc(subjectRef);
      
      if (subjectDoc.exists()) {
        const subjectData = subjectDoc.data();
        
        // Delete the image from storage if it exists
        if (subjectData.image && typeof subjectData.image === 'string' && subjectData.image.startsWith('https://')) {
          try {
            // Extract the path from the URL (this is a simplification)
            const imagePath = subjectData.image.split('?')[0].split('/o/')[1];
            if (imagePath) {
              const decodedPath = decodeURIComponent(imagePath);
              const imageRef = ref(storage, decodedPath);
              await deleteObject(imageRef);
            }
          } catch (imageError) {
            console.error("Error deleting subject image:", imageError);
            // Continue with subject deletion even if image deletion fails
          }
        }
      }
      
      // Delete the subject document
      await deleteDoc(subjectRef);
      
      return { success: true };
    } catch (error) {
      console.error("Error deleting subject:", error);
      throw error;
    }
  };
  
  // Update subject progress
  export const updateSubjectProgress = async (subjectId, completedTopics) => {
    try {
      const subjectRef = doc(db, "subjects", subjectId);
      const subjectDoc = await getDoc(subjectRef);
      
      if (!subjectDoc.exists()) {
        throw new Error("Subject not found");
      }
      
      const subjectData = subjectDoc.data();
      const totalTopics = subjectData.topics || 0;
      
      // Calculate progress percentage
      const progress = totalTopics > 0 ? Math.round((completedTopics / totalTopics) * 100) : 0;
      
      await updateDoc(subjectRef, {
        completed: completedTopics,
        progress,
        updatedAt: serverTimestamp()
      });
      
      return {
        id: subjectId,
        ...subjectData,
        completed: completedTopics,
        progress
      };
    } catch (error) {
      console.error("Error updating subject progress:", error);
      throw error;
    }
  };

  export const fetchSubjectDetails = async (subjectId) => {
    try {
      const subjectRef = doc(db, "subjects", subjectId);
      const subjectDoc = await getDoc(subjectRef);
      
      if (!subjectDoc.exists()) {
        throw new Error("Subject not found");
      }
      
      return {
        id: subjectDoc.id,
        ...subjectDoc.data()
      };
    } catch (error) {
      console.error("Error fetching subject details:", error);
      throw error;
    }
  };
  
  // Fetch all topics for a subject
  export const fetchTopicsForSubject = async (subjectId) => {
    try {
      const topicsRef = collection(db, "topics");
      const q = query(
        topicsRef,
        where("subjectId", "==", subjectId),
        orderBy("name")
      );
      
      const querySnapshot = await getDocs(q);
      const topics = [];
      
      querySnapshot.forEach((doc) => {
        topics.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return topics;
    } catch (error) {
      console.error("Error fetching topics:", error);
      throw error;
    }
  };
  
  // Create a new topic
  export const createTopic = async (topicData) => {
    try {
      const topicsRef = collection(db, "topics");
      const docRef = await addDoc(topicsRef, {
        ...topicData,
        completed: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // If there are related topics, we need to update them to include this topic
      if (topicData.relatedTopics && topicData.relatedTopics.length > 0) {
        const newTopic = {
          id: docRef.id,
          name: topicData.name,
          subjectId: topicData.subjectId
        };
        
        for (const relatedTopic of topicData.relatedTopics) {
          const relatedTopicRef = doc(db, "topics", relatedTopic.id);
          const relatedTopicDoc = await getDoc(relatedTopicRef);
          
          if (relatedTopicDoc.exists()) {
            const relatedTopicData = relatedTopicDoc.data();
            const currentRelatedTopics = relatedTopicData.relatedTopics || [];
            
            // Add this topic to the related topic's relatedTopics array if not already present
            const isAlreadyRelated = currentRelatedTopics.some(t => t.id === docRef.id);
            
            if (!isAlreadyRelated) {
              await updateDoc(relatedTopicRef, {
                relatedTopics: [...currentRelatedTopics, newTopic],
                updatedAt: serverTimestamp()
              });
            }
          }
        }
      }
      
      return {
        id: docRef.id,
        ...topicData,
        completed: false
      };
    } catch (error) {
      console.error("Error creating topic:", error);
      throw error;
    }
  };
  
  // Update an existing topic
  export const updateTopic = async (topicId, topicData) => {
    try {
      const topicRef = doc(db, "topics", topicId);
      
      // First get the current topic data to compare related topics
      const topicDoc = await getDoc(topicRef);
      if (!topicDoc.exists()) {
        throw new Error("Topic not found");
      }
      
      const currentTopicData = topicDoc.data();
      const currentRelatedTopics = currentTopicData.relatedTopics || [];
      const newRelatedTopics = topicData.relatedTopics || [];
      
      // Find topics that need to be added as related
      const topicsToAdd = newRelatedTopics.filter(newTopic => 
        !currentRelatedTopics.some(currentTopic => currentTopic.id === newTopic.id)
      );
      
      // Find topics that need to be removed as related
      const topicsToRemove = currentRelatedTopics.filter(currentTopic => 
        !newRelatedTopics.some(newTopic => newTopic.id === currentTopic.id)
      );
      
      // Update the topic
      await updateDoc(topicRef, {
        ...topicData,
        updatedAt: serverTimestamp()
      });
      
      // Update related topics to include this topic
      const thisTopic = {
        id: topicId,
        name: topicData.name,
        subjectId: topicData.subjectId
      };
      
      // Add this topic to newly related topics
      for (const relatedTopic of topicsToAdd) {
        const relatedTopicRef = doc(db, "topics", relatedTopic.id);
        const relatedTopicDoc = await getDoc(relatedTopicRef);
        
        if (relatedTopicDoc.exists()) {
          const relatedTopicData = relatedTopicDoc.data();
          const currentRelated = relatedTopicData.relatedTopics || [];
          
          await updateDoc(relatedTopicRef, {
            relatedTopics: [...currentRelated, thisTopic],
            updatedAt: serverTimestamp()
          });
        }
      }
      
      // Remove this topic from no-longer-related topics
      for (const relatedTopic of topicsToRemove) {
        const relatedTopicRef = doc(db, "topics", relatedTopic.id);
        const relatedTopicDoc = await getDoc(relatedTopicRef);
        
        if (relatedTopicDoc.exists()) {
          const relatedTopicData = relatedTopicDoc.data();
          const currentRelated = relatedTopicData.relatedTopics || [];
          
          await updateDoc(relatedTopicRef, {
            relatedTopics: currentRelated.filter(t => t.id !== topicId),
            updatedAt: serverTimestamp()
          });
        }
      }
      
      return {
        id: topicId,
        ...topicData
      };
    } catch (error) {
      console.error("Error updating topic:", error);
      throw error;
    }
  };
  
  // Fetch all subtopics for a topic
  export const fetchSubtopicsForTopic = async (topicId) => {
    try {
      const subtopicsRef = collection(db, "subtopics");
      const q = query(
        subtopicsRef,
        where("topicId", "==", topicId),
        orderBy("name")
      );
      
      const querySnapshot = await getDocs(q);
      const subtopics = [];
      
      querySnapshot.forEach((doc) => {
        subtopics.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return subtopics;
    } catch (error) {
      console.error("Error fetching subtopics:", error);
      throw error;
    }
  };
  
  // Create a new subtopic
  export const createSubtopic = async (subtopicData) => {
    try {
      const subtopicsRef = collection(db, "subtopics");
      const docRef = await addDoc(subtopicsRef, {
        ...subtopicData,
        completed: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Handle related topics and subtopics...
      // Similar to the createTopic function, we would update all related items
      
      return {
        id: docRef.id,
        ...subtopicData,
        completed: false
      };
    } catch (error) {
      console.error("Error creating subtopic:", error);
      throw error;
    }
  };
  
  // Update an existing subtopic
  export const updateSubtopic = async (subtopicId, subtopicData) => {
    try {
      const subtopicRef = doc(db, "subtopics", subtopicId);
      
      await updateDoc(subtopicRef, {
        ...subtopicData,
        updatedAt: serverTimestamp()
      });
      
      // Handle updates to related topics and subtopics...
      // Similar to the updateTopic function
      
      return {
        id: subtopicId,
        ...subtopicData
      };
    } catch (error) {
      console.error("Error updating subtopic:", error);
      throw error;
    }
  };
  
  // Mark a topic as completed
  export const markTopicAsCompleted = async (topicId) => {
    try {
      const topicRef = doc(db, "topics", topicId);
      
      await updateDoc(topicRef, {
        completed: true,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Get the subject ID to update its progress
      const topicDoc = await getDoc(topicRef);
      if (topicDoc.exists()) {
        const { subjectId } = topicDoc.data();
        
        // Get all topics for this subject
        const topics = await fetchTopicsForSubject(subjectId);
        const completedTopics = topics.filter(topic => topic.completed || topic.id === topicId).length;
        
        // Update subject progress
        await updateSubjectProgress(subjectId, completedTopics);
      }
      
      return { success: true };
    } catch (error) {
      console.error("Error marking topic as completed:", error);
      throw error;
    }
  };
  
  // Mark a subtopic as completed
  export const markSubtopicAsCompleted = async (subtopicId) => {
    try {
      const subtopicRef = doc(db, "subtopics", subtopicId);
      
      await updateDoc(subtopicRef, {
        completed: true,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error("Error marking subtopic as completed:", error);
      throw error;
    }
  };
